import React from 'react';
import { Navigate, Routes, Route, useParams } from 'react-router-dom';
import { Spin } from 'antd';
import PageLayout from '../UI/PageLayout';
import CompassPortal, { STAGE_ORDER } from './v2/CompassPortal';
import DataCheckPage from './DataCheckPage';
import SimpleProjectPage from './SimpleProjectPage';
import { useGetUserTypeQuery } from '../../redux/api/user';

// Compass Portal — the agent-led staged portal (v2) replaced the old page-per-stage
// portal outright (2026-07-20, Malik). Clients still get the simple guided view;
// internal/admin users get the full agent-led workspace. /check-file (the
// "Check a data file" rule-set surface) stays reachable — processing error
// messages point people at it.

const Loading = () => <div style={{ textAlign: 'center', padding: 80 }}><Spin /></div>;

// Deep link to a project (and optionally an old per-stage URL) — clients keep the
// simple view; everyone else lands in the agent-led portal at that project/stage.
const ProjectDeepLink = () => {
    const { projectId, stageId } = useParams();
    const { data: userType, isLoading } = useGetUserTypeQuery();
    if (isLoading) return <Loading />;
    if (userType === 'Client') return <SimpleProjectPage />;
    const stage = STAGE_ORDER.includes(stageId) ? stageId : undefined;
    return <CompassPortal initialProjectId={projectId} initialStageId={stage} />;
};

const InternalOnly = ({ children }) => {
    const { projectId } = useParams();
    const { data: userType, isLoading } = useGetUserTypeQuery();
    if (isLoading) return <Loading />;
    return userType === 'Client' ? <Navigate to={`/compass-portal/${projectId}`} replace /> : children;
};

const CompassPortalPage = () => {
    return (
        <PageLayout
            title="Compass Portal"
            content={
                <div style={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>
                    <Routes>
                        <Route path="/" element={<CompassPortal />} />
                        {/* A bare /compass-portal/simple has no project id — bounce home. */}
                        <Route path="/simple" element={<Navigate to="/compass-portal" replace />} />
                        <Route path="/:projectId" element={<ProjectDeepLink />} />
                        <Route path="/:projectId/simple" element={<SimpleProjectPage />} />
                        <Route path="/:projectId/check-file" element={<InternalOnly><DataCheckPage /></InternalOnly>} />
                        {/* Old per-stage URLs (e.g. /:projectId/data-collection) open the
                            agent-led portal at that stage. */}
                        <Route path="/:projectId/:stageId" element={<ProjectDeepLink />} />
                    </Routes>
                </div>
            }
        />
    );
};

export default CompassPortalPage;
